import { Badge, useTheme } from "@mui/material";
import * as React from "react";

/**
 * An icon badge intended to display the axis constraints currently applied to a tool
 */
export function AxisConstraintBadge(
  props: React.PropsWithChildren & { axisConstraint: "x" | "y" | "xy" | "" },
) {
  const theme = useTheme();
  if (!props.axisConstraint) {
    return props.children;
  }
  return (
    <Badge
      badgeContent={props.axisConstraint}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      variant="standard"
      sx={{
        // Serious font manipulation going on in here to maximize the amount of padding
        // between the text of the badge and the icon it wraps.
        // For context, take a look at https://en.wikipedia.org/wiki/Baseline_(typography).
        //
        // Because axis constraints are typed as lowercase letters, we can use
        // antigravity to take the extra line height from the top of the letters, and put that space
        // below the text as padding between it and the icon wrapped by the badge. Do this with some
        // font anatomy surgery to realign the lowercase letters from the cap height instead of the
        // baseline:
        //
        // 1ex is the height of the lowercase x, along with other lowercase letters in most fonts.
        // 0.667em is the Cap Height in many fonts: the height of capital letters from the baseline.
        // The remaining 0.333em is normally the descender, the dangly bits like g and y in dangly.
        //
        // Based on that, we inset the badge with the appropriate negative margin/positive padding.
        pt: "calc((2em/3) - 1ex)",
        mt: "calc((-2em/3) + 1ex)",

        "& .MuiBadge-badge": {
          color: theme.palette.primary.main,
          fontSize: "8pt",
          width: "10pt",
          height: "10pt",
          maxWidth: "10pt",
          maxHeight: "10pt",
        },
      }}
    >
      {props.children}
    </Badge>
  );
}
